import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageCircle, X, Send, Plus, Users, Search, Paperclip,
  ChevronLeft, MoreVertical, File, Image, Check, CheckCheck,
  Smile, Loader2, User, FileText, Building2, Circle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

const FloatingChat = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [salas, setSalas] = useState([]);
  const [salaActual, setSalaActual] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [nuevoMensaje, setNuevoMensaje] = useState('');
  const [totalNoLeidos, setTotalNoLeidos] = useState(0);
  const [usuariosOnline, setUsuariosOnline] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [showNuevoChat, setShowNuevoChat] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollingRef = useRef(null);

  // Cargar salas
  const cargarSalas = useCallback(async () => {
    try {
      const res = await api.get('/api/chat-interno/salas');
      setSalas(res.data);
    } catch (error) {
      console.error('Error cargando salas:', error);
    }
  }, []);

  // Cargar mensajes no leídos
  const cargarNoLeidos = useCallback(async () => {
    try {
      const res = await api.get('/api/chat-interno/no-leidos');
      setTotalNoLeidos(res.data.total);
    } catch (error) {
      console.error('Error cargando no leídos:', error);
    }
  }, []);

  // Cargar usuarios online
  const cargarUsuariosOnline = useCallback(async () => {
    try {
      const res = await api.get('/api/chat-interno/usuarios-online');
      setUsuariosOnline(res.data);
    } catch (error) {
      console.error('Error cargando usuarios:', error);
    }
  }, []);

  // Ping para mantenerse online
  const ping = useCallback(async () => {
    try {
      await api.post('/api/chat-interno/ping');
    } catch (error) {
      console.error('Error en ping:', error);
    }
  }, []);

  // Cargar mensajes de una sala
  const cargarMensajes = useCallback(async (salaId) => {
    try {
      setLoading(true);
      const res = await api.get(`/api/chat-interno/salas/${salaId}/mensajes`);
      setMensajes(res.data);
      await api.post(`/api/chat-interno/salas/${salaId}/marcar-leido`);
      cargarNoLeidos();
    } catch (error) {
      console.error('Error cargando mensajes:', error);
    } finally {
      setLoading(false);
    }
  }, [cargarNoLeidos]);

  // Efecto inicial
  useEffect(() => {
    if (user) {
      cargarSalas();
      cargarNoLeidos();
      cargarUsuariosOnline();
      ping();

      // Polling cada 5 segundos
      pollingRef.current = setInterval(() => {
        ping();
        cargarNoLeidos();
        cargarUsuariosOnline();
        if (salaActual) {
          cargarMensajes(salaActual.id);
        }
      }, 5000);

      return () => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
        }
        api.post('/api/chat-interno/disconnect').catch(() => {});
      };
    }
  }, [user, cargarSalas, cargarNoLeidos, cargarUsuariosOnline, ping, salaActual, cargarMensajes]);

  // Scroll al final de mensajes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensajes]);

  // Seleccionar sala
  const seleccionarSala = async (sala) => {
    setSalaActual(sala);
    await cargarMensajes(sala.id);
  };

  // Enviar mensaje
  const enviarMensaje = async (e) => {
    e.preventDefault();
    if (!nuevoMensaje.trim() || !salaActual) return;

    try {
      setEnviando(true);
      await api.post(`/api/chat-interno/salas/${salaActual.id}/mensajes`, {
        contenido: nuevoMensaje.trim(),
        tipo: 'texto'
      });
      setNuevoMensaje('');
      await cargarMensajes(salaActual.id);
      cargarSalas();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo enviar el mensaje',
        variant: 'destructive'
      });
    } finally {
      setEnviando(false);
    }
  };

  // Subir archivo
  const subirArchivo = async (e) => {
    const file = e.target.files[0];
    if (!file || !salaActual) return;

    const formData = new FormData();
    formData.append('archivo', file);

    try {
      setEnviando(true);
      await api.post(`/api/chat-interno/salas/${salaActual.id}/adjuntos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await cargarMensajes(salaActual.id);
      cargarSalas();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo subir el archivo',
        variant: 'destructive'
      });
    } finally {
      setEnviando(false);
    }
  };

  // Crear chat privado
  const crearChatPrivado = async (usuarioId) => {
    try {
      const res = await api.post('/api/chat-interno/salas', {
        tipo: 'privado',
        miembros_ids: [usuarioId]
      });
      await cargarSalas();
      const nuevaSala = salas.find(s => s.id === res.data.id) || { id: res.data.id };
      setSalaActual(nuevaSala);
      await cargarMensajes(res.data.id);
      setShowNuevoChat(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo crear el chat',
        variant: 'destructive'
      });
    }
  };

  // Crear grupo
  const crearGrupo = async (nombre, miembrosIds) => {
    try {
      const res = await api.post('/api/chat-interno/salas', {
        nombre,
        tipo: 'grupo',
        miembros_ids: miembrosIds
      });
      await cargarSalas();
      setShowNuevoChat(false);
      toast({
        title: 'Grupo creado',
        description: `Se ha creado el grupo "${nombre}"`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo crear el grupo',
        variant: 'destructive'
      });
    }
  };

  // Formatear hora
  const formatHora = (fecha) => {
    const d = new Date(fecha);
    const hoy = new Date();
    const esHoy = d.toDateString() === hoy.toDateString();
    
    if (esHoy) {
      return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
  };

  // Obtener iniciales
  const getInitials = (nombre) => {
    if (!nombre) return '?';
    return nombre.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  if (!user) return null;

  // Panel minimizado (solo botón)
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all z-50"
        data-testid="chat-floating-button"
      >
        <MessageCircle className="w-6 h-6" />
        {totalNoLeidos > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {totalNoLeidos > 9 ? '9+' : totalNoLeidos}
          </span>
        )}
      </button>
    );
  }

  return (
    <>
      {/* Panel de chat */}
      <div 
        className={`fixed bottom-6 right-6 bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden z-50 transition-all ${
          isMinimized ? 'w-80 h-14' : 'w-96 h-[32rem]'
        }`}
        data-testid="chat-panel"
      >
        {/* Header */}
        <div className="bg-blue-600 text-white px-4 py-3 flex items-center justify-between">
          {salaActual ? (
            <>
              <button 
                onClick={() => setSalaActual(null)} 
                className="p-1 hover:bg-blue-500 rounded"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="font-medium truncate flex-1 mx-2">{salaActual.nombre}</span>
            </>
          ) : (
            <>
              <MessageCircle className="w-5 h-5 mr-2" />
              <span className="font-medium flex-1">Chat Interno</span>
            </>
          )}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1 hover:bg-blue-500 rounded"
            >
              {isMinimized ? <Plus className="w-5 h-5" /> : <span className="w-5 h-5 flex items-center justify-center">−</span>}
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-blue-500 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Vista de sala */}
            {salaActual ? (
              <div className="flex flex-col h-[calc(32rem-3.5rem)]">
                {/* Mensajes */}
                <ScrollArea className="flex-1 p-3">
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                    </div>
                  ) : mensajes.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No hay mensajes aún</p>
                      <p className="text-sm">¡Envía el primero!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {mensajes.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.es_mio ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-lg px-3 py-2 ${
                              msg.es_mio
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-900'
                            }`}
                          >
                            {!msg.es_mio && (
                              <p className="text-xs font-medium mb-1 opacity-75">
                                {msg.usuario?.nombre}
                              </p>
                            )}
                            {msg.tipo === 'archivo' && msg.adjuntos?.length > 0 && (
                              <div className="flex items-center gap-2 mb-1">
                                <File className="w-4 h-4" />
                                <span className="text-sm">{msg.adjuntos[0].nombre_archivo}</span>
                              </div>
                            )}
                            {msg.contenido && (
                              <p className="text-sm whitespace-pre-wrap">{msg.contenido}</p>
                            )}
                            <div className={`flex items-center justify-end gap-1 mt-1 ${
                              msg.es_mio ? 'text-blue-200' : 'text-gray-400'
                            }`}>
                              <span className="text-xs">{formatHora(msg.created_at)}</span>
                              {msg.editado && <span className="text-xs">(editado)</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </ScrollArea>

                {/* Input */}
                <form onSubmit={enviarMensaje} className="p-3 border-t bg-gray-50">
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={subirArchivo}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={enviando}
                    >
                      <Paperclip className="w-4 h-4" />
                    </Button>
                    <Input
                      value={nuevoMensaje}
                      onChange={(e) => setNuevoMensaje(e.target.value)}
                      placeholder="Escribe un mensaje..."
                      className="flex-1 h-9"
                      disabled={enviando}
                      data-testid="chat-input"
                    />
                    <Button 
                      type="submit" 
                      size="icon" 
                      className="h-9 w-9"
                      disabled={!nuevoMensaje.trim() || enviando}
                      data-testid="chat-send-button"
                    >
                      {enviando ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            ) : (
              /* Lista de salas */
              <div className="flex flex-col h-[calc(32rem-3.5rem)]">
                {/* Buscador y nuevo chat */}
                <div className="p-3 border-b">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Buscar chat..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        className="pl-9 h-9"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => setShowNuevoChat(true)}
                      data-testid="new-chat-button"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Lista de chats */}
                <ScrollArea className="flex-1">
                  {salas.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No tienes chats</p>
                      <Button
                        variant="link"
                        onClick={() => setShowNuevoChat(true)}
                        className="text-blue-600"
                      >
                        Iniciar una conversación
                      </Button>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {salas
                        .filter(s => !busqueda || s.nombre?.toLowerCase().includes(busqueda.toLowerCase()))
                        .map((sala) => (
                          <button
                            key={sala.id}
                            onClick={() => seleccionarSala(sala)}
                            className="w-full p-3 hover:bg-gray-50 flex items-center gap-3 text-left"
                            data-testid={`chat-sala-${sala.id}`}
                          >
                            <div className="relative">
                              <Avatar className="h-10 w-10">
                                <AvatarFallback className={
                                  sala.tipo === 'grupo' ? 'bg-purple-100 text-purple-700' :
                                  sala.tipo === 'expediente' ? 'bg-amber-100 text-amber-700' :
                                  'bg-blue-100 text-blue-700'
                                }>
                                  {sala.tipo === 'grupo' ? <Users className="w-5 h-5" /> :
                                   sala.tipo === 'expediente' ? <FileText className="w-5 h-5" /> :
                                   getInitials(sala.nombre)}
                                </AvatarFallback>
                              </Avatar>
                              {sala.tipo === 'privado' && sala.miembros?.some(m => m.id !== user?.id && m.online) && (
                                <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-sm truncate">{sala.nombre}</span>
                                {sala.ultimo_mensaje_at && (
                                  <span className="text-xs text-gray-400">
                                    {formatHora(sala.ultimo_mensaje_at)}
                                  </span>
                                )}
                              </div>
                              {sala.expediente_numero && (
                                <span className="text-xs text-gray-500">Exp: {sala.expediente_numero}</span>
                              )}
                            </div>
                            {sala.no_leidos > 0 && (
                              <Badge className="bg-blue-600 text-white text-xs">
                                {sala.no_leidos}
                              </Badge>
                            )}
                          </button>
                        ))}
                    </div>
                  )}
                </ScrollArea>

                {/* Usuarios online */}
                <div className="p-3 border-t bg-gray-50">
                  <p className="text-xs text-gray-500 mb-2">
                    Usuarios online ({usuariosOnline.filter(u => u.online).length})
                  </p>
                  <div className="flex items-center gap-1 overflow-x-auto">
                    {usuariosOnline.filter(u => u.online && u.id !== user?.id).map(u => (
                      <button
                        key={u.id}
                        onClick={() => crearChatPrivado(u.id)}
                        className="flex-shrink-0"
                        title={u.nombre}
                      >
                        <Avatar className="h-8 w-8 border-2 border-green-400">
                          <AvatarFallback className="bg-gray-100 text-gray-700 text-xs">
                            {getInitials(u.nombre)}
                          </AvatarFallback>
                        </Avatar>
                      </button>
                    ))}
                    {usuariosOnline.filter(u => u.online && u.id !== user?.id).length === 0 && (
                      <span className="text-xs text-gray-400">Nadie más conectado</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal nuevo chat */}
      <NuevoChatModal
        isOpen={showNuevoChat}
        onClose={() => setShowNuevoChat(false)}
        usuarios={usuariosOnline.filter(u => u.id !== user?.id)}
        onChatPrivado={crearChatPrivado}
        onGrupo={crearGrupo}
      />
    </>
  );
};

// Componente modal para nuevo chat
const NuevoChatModal = ({ isOpen, onClose, usuarios, onChatPrivado, onGrupo }) => {
  const [tab, setTab] = useState('privado');
  const [nombreGrupo, setNombreGrupo] = useState('');
  const [miembrosSeleccionados, setMiembrosSeleccionados] = useState([]);
  const [busqueda, setBusqueda] = useState('');

  const toggleMiembro = (id) => {
    setMiembrosSeleccionados(prev =>
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    );
  };

  const crearGrupo = () => {
    if (nombreGrupo.trim() && miembrosSeleccionados.length > 0) {
      onGrupo(nombreGrupo.trim(), miembrosSeleccionados);
      setNombreGrupo('');
      setMiembrosSeleccionados([]);
    }
  };

  const usuariosFiltrados = usuarios.filter(u => 
    !busqueda || u.nombre.toLowerCase().includes(busqueda.toLowerCase())
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nueva conversación</DialogTitle>
        </DialogHeader>
        
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="privado">Chat privado</TabsTrigger>
            <TabsTrigger value="grupo">Crear grupo</TabsTrigger>
          </TabsList>
          
          <TabsContent value="privado" className="mt-4">
            <div className="mb-3">
              <Input
                placeholder="Buscar usuario..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </div>
            <ScrollArea className="h-64">
              <div className="space-y-1">
                {usuariosFiltrados.map(u => (
                  <button
                    key={u.id}
                    onClick={() => onChatPrivado(u.id)}
                    className="w-full p-2 hover:bg-gray-100 rounded-lg flex items-center gap-3"
                  >
                    <div className="relative">
                      <Avatar className="h-9 w-9">
                        <AvatarFallback>
                          {u.nombre.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      {u.online && (
                        <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full" />
                      )}
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-sm">{u.nombre}</p>
                      <p className="text-xs text-gray-500">{u.rol}</p>
                    </div>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="grupo" className="mt-4">
            <div className="space-y-3">
              <Input
                placeholder="Nombre del grupo"
                value={nombreGrupo}
                onChange={(e) => setNombreGrupo(e.target.value)}
              />
              <p className="text-sm text-gray-500">Selecciona miembros:</p>
              <ScrollArea className="h-48">
                <div className="space-y-1">
                  {usuarios.map(u => (
                    <button
                      key={u.id}
                      onClick={() => toggleMiembro(u.id)}
                      className={`w-full p-2 rounded-lg flex items-center gap-3 ${
                        miembrosSeleccionados.includes(u.id) ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-100'
                      }`}
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarFallback>
                          {u.nombre.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="flex-1 text-left text-sm">{u.nombre}</span>
                      {miembrosSeleccionados.includes(u.id) && (
                        <Check className="w-4 h-4 text-blue-600" />
                      )}
                    </button>
                  ))}
                </div>
              </ScrollArea>
              <Button
                onClick={crearGrupo}
                disabled={!nombreGrupo.trim() || miembrosSeleccionados.length === 0}
                className="w-full"
              >
                <Users className="w-4 h-4 mr-2" />
                Crear grupo ({miembrosSeleccionados.length} miembros)
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default FloatingChat;
